/**
 * MCP (Model Context Protocol) API service - manages MCP tokens for Claude Desktop integration
 */

import { apiClient } from './client';

// Types
export interface MCPToken {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  tokenPrefix: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  isExpired: boolean;
}

export interface MCPTokenCreated extends MCPToken {
  token: string; // Only available at creation time
}

export interface CreateMCPTokenRequest {
  name: string;
  organizationId: string;
  expiresInDays?: number;
}

export interface MCPTokenListResponse {
  items: MCPToken[];
  total: number;
}

/**
 * MCP Tokens API methods
 */
export const mcpApi = {
  /**
   * Create a new MCP token
   */
  async create(data: CreateMCPTokenRequest): Promise<MCPTokenCreated> {
    const response = await apiClient.post<any>('/mcp-tokens', {
      name: data.name,
      organization_id: data.organizationId,
      expires_in_days: data.expiresInDays,
    });
    return transformMCPTokenCreated(response.data);
  },

  /**
   * List all MCP tokens for the current user
   */
  async list(): Promise<MCPTokenListResponse> {
    const response = await apiClient.get<any>('/mcp-tokens');
    return {
      items: response.data.items.map(transformMCPToken),
      total: response.data.total,
    };
  },

  /**
   * Get a specific MCP token by ID
   */
  async get(tokenId: string): Promise<MCPToken> {
    const response = await apiClient.get<any>(`/mcp-tokens/${tokenId}`);
    return transformMCPToken(response.data);
  },

  /**
   * Revoke (delete) an MCP token
   */
  async revoke(tokenId: string): Promise<void> {
    await apiClient.delete(`/mcp-tokens/${tokenId}`);
  },

  /**
   * Download the .mcpb bundle for Claude Desktop.
   *
   * The endpoint is authenticated, so it must be fetched through the API
   * client (which attaches the bearer token) rather than linked to directly:
   * a plain `<a href download>` navigation sends no Authorization header and
   * the browser reports the download as failed.
   */
  async downloadExtension(): Promise<Blob> {
    const response = await apiClient.get<Blob>('/mcp-tokens/download-extension', {
      responseType: 'blob',
    });
    return response.data;
  },
};

/**
 * Transform API response to camelCase
 */
function transformMCPToken(data: any): MCPToken {
  return {
    id: data.id,
    name: data.name,
    organizationId: data.organizationId ?? data.organization_id,
    organizationName: data.organizationName ?? data.organization_name,
    tokenPrefix: data.tokenPrefix ?? data.token_prefix,
    expiresAt: data.expiresAt ?? data.expires_at,
    lastUsedAt: data.lastUsedAt ?? data.last_used_at,
    createdAt: data.createdAt ?? data.created_at,
    isExpired: data.isExpired ?? data.is_expired,
  };
}

function transformMCPTokenCreated(data: any): MCPTokenCreated {
  return {
    ...transformMCPToken(data),
    token: data.token,
  };
}

export default mcpApi;
